/**
 * Fetch utility that replaces axios
 * Handles CSRF tokens, credentials, and session keys automatically.
 * Always same-origin /api — never VUE_APP_API_URL (Domain Host is the tenant).
 */

import {
  getHostCsrfToken,
  getHostSessionKey,
  setHostCsrfToken,
} from '@/utils/hostScopedStorage'

/**
 * Resolve API base URL. In dev, tenant subdomains must use same-origin `/api` so the Vue
 * dev-server proxy forwards to Django with the browser Host (django-tenants schema).
 */
function resolveApiBaseUrl() {
  const configured = process.env.VUE_APP_API_URL || '/api'
  if (typeof window === 'undefined') return configured

  const hostname = String(window.location.hostname || '').toLowerCase()
  const isDevMultiTenantHost =
    process.env.NODE_ENV === 'development' &&
    (hostname === 'platform.localhost' || hostname.endsWith('.platform.localhost'))

  if (isDevMultiTenantHost && configured.startsWith('http')) {
    console.warn(
      '[API] VUE_APP_API_URL points at another host; using /api via Vue dev proxy for',
      hostname,
    )
    return '/api'
  }
  return configured
}

const API_URL = resolveApiBaseUrl()

/**
 * Get CSRF token from storage (prioritize backend-provided token)
 * The token should come from the backend (login response or /auth/csrf-token/)
 * not from cookies, as cookies may have domain/path restrictions
 */
function getCsrfToken() {
  const token = getHostCsrfToken()
  if (token) {
    return token
  }
  // Last resort: try cookie (host-only, scoped to current tenant)
  const cookies = document.cookie.split(';')
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === 'csrftoken' && value) {
      setHostCsrfToken(value)
      return value
    }
  }
  return null
}

function saveCsrfToken(token) {
  setHostCsrfToken(token)
}

function getSessionKey() {
  return getHostSessionKey()
}

/**
 * Fetch wrapper that handles authentication and CSRF
 * @param {string} url
 * @param {object} options - fetch options plus optional `params`, `timeout` (ms)
 */
async function apiFetch(url, options = {}) {
  const { timeout, params, ...restOptions } = options
  let fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`
  if (params && typeof params === 'object' && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') {
        searchParams.append(k, v)
      }
    }
    const qs = searchParams.toString()
    if (qs) {
      fullUrl += (fullUrl.includes('?') ? '&' : '?') + qs
    }
  }

  // Get CSRF token
  const csrfToken = getCsrfToken()
  
  // Get session key
  const sessionKey = getSessionKey()
  
  // Prepare headers (always use host-scoped session — ignore legacy localStorage headers from callers)
  const headers = {
    ...restOptions.headers,
  }
  delete headers['X-Session-Key']
  delete headers['x-session-key']

  // Dev multi-tenant: ensure django-tenants sees the browser hostname even if
  // the webpack proxy forwards localhost as Host.
  if (typeof window !== 'undefined' && window.location.hostname) {
    headers['X-Clinch-Tenant-Host'] = window.location.hostname
  }
  
  // Only set Content-Type for JSON, not for FormData (browser will set it with boundary).
  // Strip any caller-provided Content-Type on FormData — bare multipart/form-data
  // without boundary makes Django return "Invalid boundary in multipart: None".
  if (restOptions.body instanceof FormData) {
    delete headers['Content-Type']
    delete headers['content-type']
  } else {
    headers['Content-Type'] = 'application/json'
  }
  
  // Add CSRF token if available
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken
  }
  
  // Add session key if available
  if (sessionKey) {
    headers['X-Session-Key'] = sessionKey
  }

  const controller =
    timeout && Number(timeout) > 0 && !restOptions.signal
      ? new AbortController()
      : null
  let timeoutId = null
  if (controller) {
    timeoutId = setTimeout(() => controller.abort(), Number(timeout))
  }
  
  // Prepare fetch options
  const fetchOptions = {
    ...restOptions,
    headers,
    credentials: 'include', // Always send cookies
    ...(controller ? { signal: controller.signal } : {}),
  }

  try {
    // Make the request
    const response = await fetch(fullUrl, fetchOptions)
  
  // Check if response has CSRF token in Set-Cookie header
  const setCookie = response.headers.get('Set-Cookie')
  if (setCookie && setCookie.includes('csrftoken=')) {
    // Extract CSRF token from Set-Cookie header
    const match = setCookie.match(/csrftoken=([^;]+)/)
    if (match && match[1]) {
      saveCsrfToken(match[1])
    }
  }
  
  // Parse body. Honor axios-style responseType for binary downloads (xlsx, etc.).
  // Defaulting binary to response.text() corrupts files and Excel reports them as corrupt.
  let data
  const responseType = (restOptions.responseType || '').toLowerCase()
  const contentType = (response.headers.get('content-type') || '').toLowerCase()
  if (responseType === 'blob') {
    data = await response.blob()
  } else if (responseType === 'arraybuffer') {
    data = await response.arrayBuffer()
  } else if (contentType.includes('application/json')) {
    try {
      data = await response.json()
      // If response contains csrfToken, save it (this is the source of truth from backend)
      if (data && data.csrfToken) {
        saveCsrfToken(data.csrfToken)
      }
    } catch (e) {
      data = null
    }
  } else if (
    contentType.includes('application/octet-stream') ||
    contentType.includes('spreadsheetml') ||
    contentType.includes('application/zip') ||
    contentType.includes('application/pdf') ||
    contentType.startsWith('image/') ||
    contentType.startsWith('audio/') ||
    contentType.startsWith('video/')
  ) {
    data = await response.blob()
  } else {
    data = await response.text()
  }
  
  // Handle CSRF errors - try to fetch fresh token from backend
  if (!response.ok && response.status === 403) {
    const errorDetail = data?.detail || ''
    if (errorDetail.includes('CSRF') || errorDetail.includes('csrf')) {
      // CSRF error - fetch fresh token from backend endpoint
      console.warn('[API] CSRF error detected, fetching fresh token from backend...')
      
      try {
        const tokenResponse = await fetch(`${API_URL}/auth/csrf-token/`, {
          method: 'GET',
          credentials: 'include'
        })
        if (tokenResponse.ok) {
          const tokenData = await tokenResponse.json()
          if (tokenData.csrfToken) {
            saveCsrfToken(tokenData.csrfToken)
            // Retry the original request with fresh token
            headers['X-CSRFToken'] = tokenData.csrfToken
            fetchOptions.headers = headers
            const retryResponse = await fetch(fullUrl, fetchOptions)
            if (retryResponse.ok) {
              // Retry succeeded, re-parse response
              const retryContentType = (retryResponse.headers.get('content-type') || '').toLowerCase()
              let retryData
              if (responseType === 'blob') {
                retryData = await retryResponse.blob()
              } else if (responseType === 'arraybuffer') {
                retryData = await retryResponse.arrayBuffer()
              } else if (retryContentType.includes('application/json')) {
                retryData = await retryResponse.json()
                // Save CSRF token if present in retry response
                if (retryData && retryData.csrfToken) {
                  saveCsrfToken(retryData.csrfToken)
                }
              } else if (
                retryContentType.includes('spreadsheetml') ||
                retryContentType.includes('application/octet-stream') ||
                retryContentType.includes('application/zip') ||
                retryContentType.includes('application/pdf')
              ) {
                retryData = await retryResponse.blob()
              } else {
                retryData = await retryResponse.text()
              }
              return { data: retryData, status: retryResponse.status, headers: retryResponse.headers }
            }
          }
        }
      } catch (tokenError) {
        console.error('[API] Failed to refresh CSRF token from backend:', tokenError)
      }
    }
  }
  
  // Handle errors
  if (!response.ok) {
    let detail = data?.detail
    if (Array.isArray(detail)) detail = detail.filter(Boolean).join(' ')
    if (detail && typeof detail === 'object') {
      try {
        detail = JSON.stringify(detail)
      } catch (_) {
        detail = String(detail)
      }
    }
    // DRF field errors arrive as { field: ["msg", ...] } with no top-level
    // `detail`. Flatten them into a readable message instead of "HTTP 400".
    if (!detail && data && typeof data === 'object' && !Array.isArray(data)) {
      const parts = []
      for (const [field, msgs] of Object.entries(data)) {
        if (field === 'csrfToken' || field === 'code') continue
        const text = Array.isArray(msgs) ? msgs.join(' ') : String(msgs)
        if (text) parts.push(field === 'non_field_errors' ? text : `${field}: ${text}`)
      }
      if (parts.length) detail = parts.join(' · ')
    }
    const error = new Error(detail || data?.error || `HTTP ${response.status}`)
    error.status = response.status
    error.response = { data, status: response.status }
    if (response.status === 402 && data?.code === 'credit_wallet_empty') {
      try {
        const { applyCreditWalletPayload } = await import('@/utils/creditWalletAi')
        applyCreditWalletPayload({
          billed_ai_blocked: data.billed_ai_blocked === true,
          billed_whatsapp_blocked: data.billed_whatsapp_blocked !== false,
          billed_ai_enabled: data.billed_ai_enabled,
          billed_whatsapp_enabled: data.billed_whatsapp_enabled,
          balance: data.balance ?? '0.00',
        })
      } catch {
        // lock helper is optional on this path
      }
    }
    throw error
  }
  
  return { data, status: response.status, headers: response.headers }
  } catch (err) {
    if (err?.name === 'AbortError') {
      const error = new Error('Request timed out. Try again or reduce audience size.')
      error.status = 408
      error.response = { data: { detail: error.message }, status: 408 }
      throw error
    }
    throw err
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

/**
 * Convenience methods
 */
export const api = {
  get: (url, options = {}) => apiFetch(url, { ...options, method: 'GET' }),
  post: (url, data, options = {}) => apiFetch(url, {
    ...options,
    method: 'POST',
    body: data instanceof FormData ? data : JSON.stringify(data)
  }),
  put: (url, data, options = {}) => apiFetch(url, {
    ...options,
    method: 'PUT',
    body: data instanceof FormData ? data : JSON.stringify(data)
  }),
  patch: (url, data, options = {}) => apiFetch(url, {
    ...options,
    method: 'PATCH',
    body: data instanceof FormData ? data : JSON.stringify(data)
  }),
  delete: (url, options = {}) => apiFetch(url, { ...options, method: 'DELETE' }),
}

export { saveCsrfToken, getCsrfToken, getSessionKey, API_URL }
export default api
